import { formatInTimeZone } from 'date-fns-tz';
import { TimeZoneDto } from '../models/dto';
import { TimeZonesEnum } from '../enums';
import { regionsLocales, timeZonesLocales } from '../constants';

export const getTimeZones = (startDate?: Date | null, timeZone?: string): Record<string, TimeZoneDto> => {
	const referenceDate = startDate ?? new Date();

	// To get the standard time zone offset ignoring daylight saving time.
	const {
		tzid: localTimeZone,
		abbr: localTimeZoneAbbr,
		offset: localTimeZoneOffset,
	} = getLocalTimeZoneInfo(referenceDate);

	const timeZones = Object.values(TimeZonesEnum).reduce<Record<string, TimeZoneDto>>(
		(acc, timeZone) => ({
			...acc,
			[timeZone]: getTimeZoneAbbrAndOffset(referenceDate, timeZone),
		}),
		{}
	);

	if (!timeZones[localTimeZone]) {
		timeZones[localTimeZone] = {
			abbr: localTimeZoneAbbr,
			offset: localTimeZoneOffset,
		};
	}

	if (timeZone && !timeZones[timeZone]) {
		const { abbr: timeZoneAbbr, offset: timeZoneOffset } = getTimeZoneAbbrAndOffset(referenceDate, timeZone);

		timeZones[timeZone] = {
			abbr: timeZoneAbbr,
			offset: timeZoneOffset,
		};
	}

	return timeZones;
};

export const getTimeZoneAbbrAndOffset = (referenceDate: Date, timeZone: string): { abbr: string; offset: string } => {
	const region = timeZone.split('/')[0];
	const locale = timeZonesLocales[timeZone] ?? regionsLocales[region] ?? 'en-US';

	const formatter = new Intl.DateTimeFormat(locale, {
		timeZone,
		timeZoneName: 'short',
	});
	const parts = formatter.formatToParts(referenceDate);
	const timeZoneAbbr = parts.find(p => p.type === 'timeZoneName')?.value;

	return {
		abbr: timeZoneAbbr ?? formatInTimeZone(referenceDate, timeZone, 'zzz'),
		offset: formatInTimeZone(referenceDate, timeZone, 'XXX'),
	};
};

export const getLocalTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || TimeZonesEnum.Central;

export const getLocalTimeZoneInfo = (
	referenceDate: Date
): {
	tzid: string;
	abbr: string;
	offset: string;
} => {
	const localTimeZone = getLocalTimeZone();

	const { abbr: localTimeZoneAbbr, offset: localTimeZoneOffset } = getTimeZoneAbbrAndOffset(
		referenceDate,
		localTimeZone
	);

	return {
		tzid: localTimeZone,
		abbr: localTimeZoneAbbr,
		offset: localTimeZoneOffset,
	};
};
